import { useState, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslate } from '@tolgee/react';
import { Button } from '~/components/ui/Button';
import { clsx } from 'clsx';
import { WrapperWidget } from '@agentselly/ui';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import type { ContactFormType } from '@agentselly/immodossier-shared';
import {
  ActionSchema,
  AppAction,
  AppRoute,
} from '@agentselly/immodossier-shared';
import { fetchBackend } from '~/helper/fetchBackend';
import { toast } from 'sonner';

const defaultMessageMinLength = 20;

interface Props {
  readonly isAuthenticated: boolean;
  readonly messageMinLength?: number;
}

export function ContactFormWidget(props: Props) {
  const { t } = useTranslate();
  const queryClient = useQueryClient();
  const [
    isSubmitting,
    setIsSubmitting,
  ] = useState(false);

  const form = useForm<ContactFormType>({
    defaultValues: {
      message: '',
      contactType: 'email',
      email: '',
    },
    resolver: zodResolver(ActionSchema[AppAction.ContactForm]),
  });

  const onSubmit = useCallback(
    async (data: ContactFormType) => {
      setIsSubmitting(true);

      try {
        const formData = new FormData();
        formData.append('message', data.message);
        formData.append('contactType', data.contactType);

        if (data.email) {
          formData.append('email', data.email);
        }

        const response = await fetchBackend(
          `/action/${AppAction.ContactForm}`,
          { method: 'POST', headers: {}, body: formData },
        );

        if (!response.ok) {
          throw new Error(
            `FormSubmit: ${response.status} ${response.statusText}`,
          );
        }

        const zodResult = await response.json();

        if (zodResult.success) {
          form.reset();
          toast.success(t('contact_message_sent'));
          await queryClient.invalidateQueries({
            queryKey: [AppRoute.$LANG_$COMPANYID_$CONTACTID_CONTACT],
          });
        } else {
          for (const [
            key,
            errors,
          ] of Object.entries(zodResult.errors || {})) {
            form.setError(key as any, {
              type: 'server',
              message: (errors as string[]).join(', '),
            });
          }
          toast.error(t('contact_message_not_sent'));
        }
      } catch (error) {
        console.error('Submission error:', error);
        toast.error(t('contact_message_not_sent'));
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      form,
      t,
      queryClient,
    ],
  );

  const contactType = form.watch('contactType');
  const messageMinLength = props.messageMinLength ?? defaultMessageMinLength;

  return (
    <WrapperWidget>
      <div className="h-full flex flex-col gap-y-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
            <svg
              className="w-5 h-5 text-primary"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"
              />
            </svg>
          </div>
          <div>
            <h2 className="text-lg font-bold">{t('contact_form_title')}</h2>
            <p className="text-sm text-muted-foreground">
              {t(
                'contact_form_subtitle',
                'Wir melden uns innerhalb von 24 Stunden bei Ihnen.',
              )}
            </p>
          </div>
        </div>

        <form
          method="post"
          onSubmit={form.handleSubmit(onSubmit)}
          className={clsx('flex flex-col gap-5 w-full', {
            'animate-pulse pointer-events-none': isSubmitting,
          })}
        >
          <div className="form-control">
            <label className="label pb-1.5">
              <span className="label-text font-medium">
                {t('contact_form_message_label')}
              </span>
            </label>
            <textarea
              {...form.register('message')}
              placeholder={t('contact_form_message_placeholder')}
              className="textarea textarea-bordered h-36 resize-y focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30 focus-visible:border-primary transition-all duration-200"
            />
            <Error
              name="message"
              formState={form.formState}
            />
          </div>

          <div className="form-control">
            <label className="label pb-1.5">
              <span className="label-text font-medium">
                {t('contact_form_contact_type_label')}
              </span>
            </label>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 cursor-pointer p-2 rounded-lg border border-base-300 hover:border-primary/50 hover:bg-base-200/50 transition-all duration-200 has-[:checked]:border-primary has-[:checked]:bg-primary/5">
                <input
                  type="radio"
                  {...form.register('contactType')}
                  value="email"
                  className="radio radio-primary radio-sm"
                />
                <span className="label-text">
                  {t('contact_form_contact_type_email')}
                </span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer p-2 rounded-lg border border-base-300 hover:border-primary/50 hover:bg-base-200/50 transition-all duration-200 has-[:checked]:border-primary has-[:checked]:bg-primary/5">
                <input
                  type="radio"
                  {...form.register('contactType')}
                  value="phone"
                  className="radio radio-primary radio-sm"
                />
                <span className="label-text">
                  {t('contact_form_contact_type_phone')}
                </span>
              </label>
            </div>
            <Error
              name="contactType"
              formState={form.formState}
            />
          </div>

          {contactType === 'email' && !props.isAuthenticated && (
            <div className="form-control">
              <label className="label pb-1.5">
                <span className="label-text font-medium">
                  {t('contact_form_email_label')}
                </span>
              </label>
              <input
                {...form.register('email')}
                type="email"
                placeholder={t('contact_form_email_placeholder')}
                className="input input-bordered focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30 focus-visible:border-primary transition-all duration-200"
              />
              <Error
                name="email"
                formState={form.formState}
              />
            </div>
          )}

          <div className="flex items-center justify-between pt-2">
            <p className="text-xs text-muted-foreground">
              {t(
                'contact_form_privacy_note',
                'Ihre Daten werden vertraulich behandelt.',
              )}
            </p>
            <Button
              type="submit"
              primary={true}
              isLoading={isSubmitting}
              showArrow
              className="md:w-auto"
            >
              {t('contact_form_submit')}
            </Button>
          </div>
        </form>
      </div>
    </WrapperWidget>
  );
}

interface ErrorProps {
  readonly name: string;
  readonly formState: any;
}

function Error(props: ErrorProps) {
  const errorMessage = props?.formState?.errors?.[props.name]?.message || '';
  if (!errorMessage) return null;
  return (
    <p className="label-text-alt text-error pt-1">{String(errorMessage)}</p>
  );
}
